import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AlertTriangle } from 'lucide-react';
import { CustomerCredit } from '@/hooks/useCustomerCredits';
import { formatCurrency } from '@/lib/utils';

const cancelSchema = z.object({
  reason: z.string().min(1, 'Cancellation reason is required'),
});

type CancelFormValues = z.infer<typeof cancelSchema>;

interface CancelCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  credit: CustomerCredit | null;
  onCancel: (data: { creditId: string; reason: string }) => void;
  isLoading: boolean;
}

const CancelCreditModal: React.FC<CancelCreditModalProps> = ({
  isOpen,
  onClose,
  credit,
  onCancel,
  isLoading,
}) => {
  const form = useForm<CancelFormValues>({
    resolver: zodResolver(cancelSchema),
    defaultValues: {
      reason: '',
    },
  });

  const handleSubmit = (values: CancelFormValues) => {
    if (!credit) return;
    onCancel({ creditId: credit.id, reason: values.reason });
    form.reset();
    onClose();
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Cancel Credit
          </DialogTitle>
        </DialogHeader>

        {credit && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 mb-4">
            <p className="text-sm text-foreground">
              You are about to cancel a credit of{' '}
              <span className="font-semibold">{formatCurrency(credit.remaining_amount)}</span>.
              This action cannot be undone.
            </p>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cancellation Reason *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g., Payment bounced, Error in credit creation..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Keep Credit
              </Button>
              <Button type="submit" variant="destructive" disabled={isLoading}>
                {isLoading ? 'Cancelling...' : 'Cancel Credit'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CancelCreditModal;
